'use client';

import { CertificationBadge } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevrondown from '@common/icon/SvgChevrondown';
import { cn } from '@core/utils';
import { useRef, useState } from 'react';

import { TERM_CATEGORY } from '@/api';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

interface FooterProps {
  className?: string;
  //* 홈 데스크톱은 이 푸터를 모달로 띄우므로, 약관 드로어를 열기 전에 그 모달을 닫아야 한다
  onOpenTerms?: () => void;
}

const LINK_CLASS_NAME = cn(
  'transition-colors duration-150 motion-reduce:transition-none',
  'hover:text-Gray-900 active:text-Gray-900',
  FOCUS_RING,
);

export const Footer = ({ className, onOpenTerms }: FooterProps) => {
  const [isBusinessInfoOpen, setIsBusinessInfoOpen] = useState(false);
  const appVersion = useDeviceInfoStore((state) => state.appVersion);
  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  const devCountRef = useRef(0);

  const router = useCustomRouter();

  const handleOpenTerms = (termCategory: TERM_CATEGORY) => {
    onOpenTerms?.();
    setTermsDrawerType(termCategory);
  };

  return (
    <footer
      id="wello-footer"
      className={cn(
        'flex justify-center bg-Gray-white px-20 pt-32 typo-caption1 xl:px-40',
        className,
      )}
      //* 하단 탭바·페이지 CTA 높이는 --bottom-inset이 모아 준다(global.scss). calc 공백 때문에 arbitrary class를 못 쓴다
      style={{ paddingBottom: 'calc(60px + var(--bottom-inset, 0px))' }}
    >
      <div className="flex w-full flex-col gap-12 xl:w-[75rem]">
        <ul className="flex items-center gap-8 pb-12">
          <li>
            <button
              type="button"
              className={cn('font-400 text-Gray-600', LINK_CLASS_NAME)}
              onClick={() => handleOpenTerms(TERM_CATEGORY.SERVICE_TERMS)}
            >
              이용약관
            </button>
          </li>
          <li aria-hidden className="h-10 w-1 bg-Gray-300" />
          <li>
            <button
              type="button"
              className={cn('font-600 text-Gray-black', LINK_CLASS_NAME)}
              onClick={() => handleOpenTerms(TERM_CATEGORY.PRIVACY_POLICY)}
            >
              개인정보처리방침
            </button>
          </li>
          <li aria-hidden className="h-10 w-1 bg-Gray-300" />
          <li>
            <button
              type="button"
              aria-expanded={isBusinessInfoOpen}
              className={cn(
                'flex items-center gap-4 font-400 text-Gray-600',
                LINK_CLASS_NAME,
              )}
              onClick={() => setIsBusinessInfoOpen((prev) => !prev)}
            >
              사업자정보
              <SvgChevrondown
                className={cn(
                  'size-10 transition-transform duration-200 motion-reduce:transition-none',
                  isBusinessInfoOpen && '-rotate-180',
                )}
              />
            </button>
          </li>
        </ul>

        {isBusinessInfoOpen && (
          <div className="flex flex-col gap-8">
            <button
              type="button"
              className="w-fit font-500 text-Gray-800 typo-body4"
              onClick={() => {
                devCountRef.current++;
                if (devCountRef.current === 10) {
                  router.push(ROUTES.TEST.pathname);
                }
              }}
            >
              주식회사 웰로
            </button>
            <ul className="flex flex-col gap-2 font-400 text-Gray-600">
              <li className="flex flex-wrap items-center gap-4">
                ㈜웰로
                <span aria-hidden className="size-2 rounded-pill bg-Gray-500" />
                대표 김유리안나
                <span aria-hidden className="size-2 rounded-pill bg-Gray-500" />
                사업자등록번호 705-86-02126
              </li>
              <li>
                통신판매업신고번호&nbsp;
                <a
                  className={cn('text-Gray-600', LINK_CLASS_NAME)}
                  href="https://www.ftc.go.kr/bizCommPop.do?wrkr_no=7058602126"
                  target="_blank"
                  rel="noreferrer external"
                >
                  제2024-세종아름-0756호
                </a>
              </li>
              <li>
                <address className="not-italic">
                  서울오피스: 서울특별시 영등포구 여의대로 108, 4층
                </address>
              </li>
              <li>
                <address className="not-italic">
                  세종오피스: 세종특별자치시 가름로 232, 세종비즈니스센터
                </address>
              </li>
              <li className="flex flex-wrap items-center gap-4">
                <a
                  href="tel:1833-9363"
                  className={cn('text-Gray-600', LINK_CLASS_NAME)}
                >
                  1833-9363
                </a>
                <span aria-hidden className="size-2 rounded-pill bg-Gray-500" />
                <a
                  href="mailto:cs@wello.info"
                  className={cn('text-Gray-600', LINK_CLASS_NAME)}
                >
                  cs@wello.info
                </a>
              </li>
            </ul>

            <CertificationBadge
              className="flex-col items-start gap-8 xl:flex-row xl:items-start xl:gap-22"
              isHideCSAP
              isHideERIVACY
            />

            <p className="font-400 text-Gray-600 typo-caption2">
              일부 콘텐츠는 AI를 활용하여 제작되었습니다.
            </p>

            {/* 시안에 없지만 CS·QA가 앱 버전을 물어볼 곳이 여기뿐이라 접힘 안에 남긴다 */}
            <p className="font-400 text-Gray-550 typo-caption2">
              ⓒ Wello Inc. {appVersion}
            </p>
          </div>
        )}

        <p className="font-400 text-Gray-600">
          본 서비스는 ㈜웰로가 제공합니다.
        </p>
        <p className="flex flex-col font-400 text-Gray-600">
          <span>
            웰로 고객센터{' '}
            <a href="tel:1833-9363" className={LINK_CLASS_NAME}>
              1833-9363
            </a>
          </span>
          <span>운영시간: 오전 10시 ~ 오후 6시 (점심시간 없음)</span>
        </p>
      </div>
    </footer>
  );
};
